import { useState, useEffect, useRef, useMemo, useCallback, createContext, useContext } from 'react';
import { createPortal } from 'react-dom';
import { BrowserRouter, Routes, Route, Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import PlantDetail from './PlantDetail';
import PlantForm from './PlantForm';
import AddPlant from './AddPlant';
import Stats from './Stats';
import Settings, { FieldOptions, HouseholdAdmin, ExportData, NotificationSettings } from './Settings';
import Wishlist from './Wishlist';
import Activity from './Activity';
import Icon from './Icons';
import './App.css';
import DataHealth from './DataHealth';
import Rounds from './Rounds';
import PropagationBay from './PropagationBay';
import Plan from './Plan';
import QAChecklist from './QAChecklist';
import OperatorConsole, { CareProfilesPage } from './Admin';
import TraitReview from './TraitReview';
import Explore, { ExploreDetail } from './Explore';
import Compare from './Compare';
import { ThemeProvider } from './ThemeContext';
import { plantDisplayName } from './nameUtils';
import { photoUrl } from './photoUtils';
import { DateFormatProvider, useDateFormat } from './DateFormatContext';
import { GuidanceProvider, useGuidance } from './GuidanceContext';
import GuidanceSheet from './GuidanceSheet';
import {
  DATA_HEALTH_GUIDANCE_TITLE, DataHealthGuidanceBody,
  WELCOME_GUIDANCE_TITLE, WelcomeGuidanceBody,
  ROUNDS_GUIDANCE_TITLE, RoundsGuidanceBody,
  PLAN_GUIDANCE_TITLE, PlanGuidanceBody,
} from './guidanceContent';
import { apiFetch, readApiError } from './api';
import { usePullToRefresh } from './hooks';
import { SkeletonCard, SkeletonLine } from './Skeleton';
import ErrorScreen from './ErrorScreen';
import { substrateComponents, formatSubstrate } from './GrowingMethodPicker';
import { formatStatus, statusStyle, useStatusMeta } from './statuses';

// Parse a UTC datetime string from SQLite (no timezone suffix) into a JS Date.
function parseUTC(str) {
  if (!str) return null;
  // SQLite stores "YYYY-MM-DD HH:MM:SS" in UTC; acquisition_date is date-only.
  // Normalize all three forms to a valid ISO string with explicit Z.
  if (str.includes('T')) return new Date(str);
  if (str.includes(' ')) return new Date(str.replace(' ', 'T') + 'Z');
  return new Date(str + 'T00:00:00Z'); // date-only: YYYY-MM-DD
}

// Format a UTC datetime string for display in local time — date + time.
function formatDateTime(str) {
  const d = parseUTC(str);
  if (!d) return '—';
  return d.toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
}

// -- Sort config ------------------------------------------------------------

const METHOD_COLORS = [
  '#2563eb', // blue
  '#16a34a', // green
  '#d97706', // amber
  '#7c3aed', // violet
  '#0891b2', // cyan
  '#c2410c', // orange
  '#be185d', // pink
  '#ca8a04', // yellow-gold
];

const SORT_OPTIONS = [
  { value: 'name',            label: 'Name' },
  { value: 'specimen_id',     label: 'Specimen ID' },
  { value: 'genus',           label: 'Genus' },
  { value: 'acquisition_date',label: 'Date Acquired' },
  { value: 'created_at',      label: 'Date Added' },
  { value: 'last_activity',   label: 'Last Activity' },
  { value: 'cost',            label: 'Cost' },
];

function sortPlants(plants, field, dir) {
  return [...plants].sort((a, b) => {
    let av, bv;

    if (field === 'name') {
      av = plantDisplayName(a).toLowerCase();
      bv = plantDisplayName(b).toLowerCase();
    } else if (field === 'acquisition_date' || field === 'created_at' || field === 'last_activity') {
      // Nulls always sort last regardless of direction
      av = a[field] ? parseUTC(a[field]).getTime() : null;
      bv = b[field] ? parseUTC(b[field]).getTime() : null;
      if (av === null && bv === null) return 0;
      if (av === null) return 1;
      if (bv === null) return -1;
    } else if (field === 'cost') {
      av = a.cost ?? -Infinity;
      bv = b.cost ?? -Infinity;
    } else {
      av = (a[field] ?? '').toString().toLowerCase();
      bv = (b[field] ?? '').toString().toLowerCase();
    }

    if (av < bv) return dir === 'asc' ? -1 : 1;
    if (av > bv) return dir === 'asc' ?  1 : -1;
    return 0;
  });
}

function getCollectionSectionKey(plant, sortField, dateFmt) {
  // Date-only strings from SQLite are treated as local midnight (no 'Z' suffix)
  // so section grouping never shifts by timezone.
  function monthYear(str) {
    if (!str) return '—';
    const d = new Date(str.includes(' ') ? str.replace(' ', 'T') + 'Z' : str + 'T00:00:00');
    if (dateFmt === 'iso') return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const locale = dateFmt === 'dmy' ? 'en-GB' : dateFmt === 'mdy' ? 'en-US' : undefined;
    return d.toLocaleDateString(locale, { month: 'short', year: 'numeric' });
  }
  if (sortField === 'name') {
    const name = plantDisplayName(plant);
    return ((name || '?')[0]).toUpperCase();
  }
  if (sortField === 'acquisition_date') return monthYear(plant.acquisition_date);
  if (sortField === 'created_at')       return monthYear(plant.created_at);
  if (sortField === 'last_activity')    return monthYear(plant.last_activity);
  if (sortField === 'cost') {
    if (plant.cost == null) return '—';
    const bucket = Math.floor(plant.cost / 25) * 25;
    return `$${bucket}–$${bucket + 25}`;
  }
  return '?';
}

function usesGenusCollectionSections(sortField) {
  return sortField === 'genus' || sortField === 'specimen_id';
}

export function buildCollectionSections(plants, sortField = 'genus', sortDir = 'asc', dateFmt = 'auto') {
  if (!usesGenusCollectionSections(sortField)) {
    const groups = [];
    let currentKey = null;

    for (const plant of plants || []) {
      const key = getCollectionSectionKey(plant, sortField, dateFmt);
      if (key !== currentKey) {
        currentKey = key;
        groups.push({ key, plants: [] });
      }
      groups[groups.length - 1].plants.push(plant);
    }

    return groups;
  }

  const grouped = new Map();
  for (const plant of plants || []) {
    const genus = plant.genus || 'Unknown genus';
    if (!grouped.has(genus)) grouped.set(genus, []);
    grouped.get(genus).push(plant);
  }

  return [...grouped.entries()]
    .sort(([a], [b]) => sortDir === 'asc' ? a.localeCompare(b) : b.localeCompare(a))
    .map(([genus, groupPlants]) => ({
      key: genus,
      plants: sortPlants(groupPlants, sortField === 'genus' ? 'name' : sortField, sortDir),
    }));
}

function PlantList() {
  const [plants, setPlants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const { statuses: statusMetadata, byKey: statusByKey } = useStatusMeta();
  const { formatDate, dateFmt } = useDateFormat();
  const { hasSeen } = useGuidance();

  // Session-dismissible data health banner
  const [gapCount,             setGapCount]             = useState(0);
  const [bannerDismissed,      setBannerDismissed]      = useState(
    () => sessionStorage.getItem('healthDismissed') === '1'
  );
  const [healthDismissedVisible, setHealthDismissedVisible] = useState(false);
  const dismissBanner = () => {
    sessionStorage.setItem('healthDismissed', '1');
    setBannerDismissed(true);
    setHealthDismissedVisible(true);
  };

  const [showWelcome, setShowWelcome] = useState(() => sessionStorage.getItem('aroidex_first_login') === '1');

  // Day-dismissible stale plants banner
  const todayISO = new Date().toISOString().slice(0, 10);
  const [staleCount,            setStaleCount]            = useState(0);
  const [staleMaxDays,          setStaleMaxDays]          = useState(0);
  const [staleBannerDismissed,  setStaleBannerDismissed]  = useState(
    () => localStorage.getItem('staleBannerDismissedDate') === todayISO
  );
  const [staleDismissedVisible, setStaleDismissedVisible] = useState(false);
  const dismissStaleBanner = () => {
    localStorage.setItem('staleBannerDismissedDate', todayISO);
    setStaleBannerDismissed(true);
    setStaleDismissedVisible(true);
  };

  // Search
  // Restore filter/sort state from sessionStorage so navigating to a plant
  // and back preserves the active filters.
  const restoreState = () => {
    try { return JSON.parse(sessionStorage.getItem('plantListState') || '{}'); } catch { return {}; }
  };
  const saved = restoreState();

  const [search,          setSearch]          = useState(saved.search          ?? '');
  const [sortField,       setSortField]       = useState(saved.sortField       ?? 'genus');
  const [sortDir,         setSortDir]         = useState(saved.sortDir         ?? 'asc');
  const [filtersOpen,     setFiltersOpen]     = useState(saved.filtersOpen     ?? false);
  const [sectionNavState,  setSectionNavState] = useState({ active: false, currentIndex: 0 });
  const [statusFilter,    setStatusFilter]    = useState(new Set(saved.statusFilter    ?? []));
  const [methodFilter,    setMethodFilter]    = useState(new Set(saved.methodFilter    ?? []));
  const [substrateFilter, setSubstrateFilter] = useState(new Set(saved.substrateFilter ?? []));
  const [locationFilter,  setLocationFilter]  = useState(new Set(saved.locationFilter  ?? []));
  const [lightFilter,     setLightFilter]     = useState(new Set(saved.lightFilter     ?? []));
  const [genusFilter,     setGenusFilter]     = useState(new Set(saved.genusFilter     ?? []));

  // Persist state to sessionStorage whenever anything changes
  useEffect(() => {
    sessionStorage.setItem('plantListState', JSON.stringify({
      search, sortField, sortDir, filtersOpen,
      statusFilter:    [...statusFilter],
      methodFilter:    [...methodFilter],
      substrateFilter: [...substrateFilter],
      locationFilter:  [...locationFilter],
      lightFilter:     [...lightFilter],
      genusFilter:     [...genusFilter],
    }));
  }, [search, sortField, sortDir, filtersOpen, statusFilter, methodFilter, substrateFilter, locationFilter, lightFilter, genusFilter]);

  const fetchPlants = useCallback(() => {
    setLoading(true);
    const plantsP = fetch('/plants')
      .then(r => { if (!r.ok) throw new Error('Failed to fetch plants'); return r.json(); })
      .then(data => { setPlants(data); setLoading(false); })
      .catch(err => { setError(err.message); setLoading(false); });

    // Fetch gap count for banner — only count plants with must/nice gaps, not optional-only
    fetch('/plants/gaps')
      .then(r => r.ok ? r.json() : [])
      .then(gaps => {
        const meaningful = gaps.filter(p =>
          p.gaps.some(g => g.priority === 'must' || g.priority === 'nice')
        );
        setGapCount(meaningful.length);
      })
      .catch(() => {});

    // Fetch stale plants count for banner
    fetch('/api/plants/needs-observation')
      .then(r => r.ok ? r.json() : [])
      .then(data => { setStaleCount(data.length); setStaleMaxDays(data[0]?.days_since ?? 0); })
      .catch(() => {});

    return plantsP;
  }, []);

  useEffect(() => {
    fetchPlants().then(() => {
      const savedScroll = sessionStorage.getItem('plantListScroll');
      if (savedScroll) {
        requestAnimationFrame(() => window.scrollTo(0, parseInt(savedScroll)));
        sessionStorage.removeItem('plantListScroll');
      }
    });
  }, [fetchPlants]);

  const refreshing = usePullToRefresh(fetchPlants);
  const sectionNavTimerRef = useRef(null);

  // -- Derived option lists (from actual data) -------------------------------

  const statuses   = useMemo(() => {
    const seen = new Set(plants.map(p => p.status));
    return statusMetadata.filter(s => seen.has(s.key) && !s.is_wishlist).map(s => s.key);
  }, [plants, statusMetadata]);
  const methods    = useMemo(() => [...new Set(plants.map(p => p.growing_method).filter(Boolean))].sort(), [plants]);
  const genera     = useMemo(() => [...new Set(plants.map(p => p.genus).filter(Boolean))].sort(), [plants]);

  // Map each growing method to a stable color from the palette
  const methodColorMap = useMemo(() => {
    const m = new Map();
    methods.forEach((method, i) => m.set(method, METHOD_COLORS[i % METHOD_COLORS.length]));
    return m;
  }, [methods]);

  // Map each method to the substrates actually used with it (derived from plant data)
  const methodSubstrateMap = useMemo(() => {
    const map = new Map();
    plants.forEach(p => {
      if (!p.growing_method || !p.substrate) return;
      if (!map.has(p.growing_method)) map.set(p.growing_method, new Set());
      substrateComponents(p.substrate).forEach(name => map.get(p.growing_method).add(name));
    });
    return map;
  }, [plants]);

  // Substrate groups visible in the filter — respects active method filter
  const visibleSubstrateGroups = useMemo(() => {
    const groups = [];
    for (const [method, subs] of methodSubstrateMap.entries()) {
      if (methodFilter.size > 0 && !methodFilter.has(method)) continue;
      groups.push({ method, substrates: [...subs].sort() });
    }
    return groups.sort((a, b) => a.method.localeCompare(b.method));
  }, [methodSubstrateMap, methodFilter]);

  // When method filter changes, drop any substrate selections that no longer belong to visible groups
  useEffect(() => {
    if (substrateFilter.size === 0) return;
    const validSubs = new Set(visibleSubstrateGroups.flatMap(g => g.substrates));
    const orphans = [...substrateFilter].filter(s => !validSubs.has(s));
    if (orphans.length > 0) {
      setSubstrateFilter(prev => {
        const next = new Set(prev);
        orphans.forEach(s => next.delete(s));
        return next;
      });
    }
  }, [visibleSubstrateGroups]); // eslint-disable-line react-hooks/exhaustive-deps

  const locations = useMemo(() => {
    const map = new Map();
    plants.forEach(p => p.locations.forEach(l => map.set(l.id, l.name)));
    return [...map.entries()].map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [plants]);

  const lightSources = useMemo(() => {
    const map = new Map();
    plants.forEach(p => p.light_sources.forEach(l => map.set(l.id, l.name)));
    return [...map.entries()].map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [plants]);

  // -- Toggle helpers for multi-select sets ---------------------------------

  function toggleSet(setter, value) {
    setter(prev => {
      const next = new Set(prev);
      next.has(value) ? next.delete(value) : next.add(value);
      return next;
    });
  }

  // -- Filter + sort pipeline -----------------------------------------------

  const filteredPlants = useMemo(() => {
    const query = search.toLowerCase().trim();

    const filtered = plants.filter(plant => {
      // Wishlist lives on its own screen — always excluded from the collection view
      if (statusByKey[plant.status]?.is_wishlist) return false;

      if (query) {
        const searchable = [plant.common_name, plant.trade_name, plant.genus, plant.species, plant.specimen_id]
          .filter(Boolean).join(' ').toLowerCase();
        if (!searchable.includes(query)) return false;
      }
      if (statusFilter.size > 0 && !statusFilter.has(plant.status)) return false;
      if (methodFilter.size > 0 && !methodFilter.has(plant.growing_method)) return false;
      if (substrateFilter.size > 0) {
        const comps = substrateComponents(plant.substrate);
        if (!comps.some(c => substrateFilter.has(c))) return false;
      }
      if (locationFilter.size > 0 && !plant.locations.some(l => locationFilter.has(l.id))) return false;
      if (lightFilter.size > 0 && !plant.light_sources.some(l => lightFilter.has(l.id))) return false;
      if (genusFilter.size > 0 && !genusFilter.has(plant.genus)) return false;
      return true;
    });

    return sortPlants(filtered, sortField, sortDir);
  }, [plants, search, statusFilter, methodFilter, substrateFilter, locationFilter, lightFilter, genusFilter, sortField, sortDir, statusByKey]);

  const activeFilterCount = useMemo(() => {
    return statusFilter.size + methodFilter.size + substrateFilter.size +
           locationFilter.size + lightFilter.size + genusFilter.size;
  }, [statusFilter, methodFilter, substrateFilter, locationFilter, lightFilter, genusFilter]);

  const hasActiveFilters = activeFilterCount > 0 || !!search;

  // -- Scroll sections -------------------------------------------------------

  const sections = useMemo(
    () => buildCollectionSections(filteredPlants, sortField, sortDir, dateFmt),
    [filteredPlants, sortField, sortDir, dateFmt]
  );
  const showCollectionSectionNav = sections.length > 1;

  const getCurrentCollectionSectionIndex = useCallback(() => {
    const sectionElements = [...document.querySelectorAll('[data-collection-section]')];
    if (sectionElements.length === 0) return 0;

    const anchorOffset = 118;
    return sectionElements.reduce((best, section, index) => {
      const distance = Math.abs(section.getBoundingClientRect().top - anchorOffset);
      return distance < best.distance ? { index, distance } : best;
    }, { index: 0, distance: Infinity }).index;
  }, []);

  const wakeCollectionSectionNav = useCallback(() => {
    if (sectionNavTimerRef.current) window.clearTimeout(sectionNavTimerRef.current);
    setSectionNavState({ active: true, currentIndex: getCurrentCollectionSectionIndex() });
    sectionNavTimerRef.current = window.setTimeout(() => {
      setSectionNavState(prev => ({ ...prev, active: false }));
    }, 2200);
  }, [getCurrentCollectionSectionIndex]);

  const jumpCollectionSection = (direction) => {
    const sectionElements = [...document.querySelectorAll('[data-collection-section]')];
    if (sectionElements.length <= 1) return;

    const currentIndex = getCurrentCollectionSectionIndex();
    const nextIndex = Math.max(0, Math.min(sectionElements.length - 1, currentIndex + direction));
    sectionElements[nextIndex]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    wakeCollectionSectionNav();
  };

  useEffect(() => {
    if (!showCollectionSectionNav) return undefined;

    let frame = null;
    const updateCurrentSection = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(() => {
        frame = null;
        setSectionNavState(prev => ({ ...prev, currentIndex: getCurrentCollectionSectionIndex() }));
      });
    };
    const wakeFromInteraction = () => {
      updateCurrentSection();
      wakeCollectionSectionNav();
    };

    updateCurrentSection();
    window.addEventListener('scroll', wakeFromInteraction, { passive: true });
    window.addEventListener('resize', updateCurrentSection);
    document.addEventListener('keydown', wakeFromInteraction);
    document.addEventListener('touchstart', wakeFromInteraction, { passive: true });

    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      window.removeEventListener('scroll', wakeFromInteraction);
      window.removeEventListener('resize', updateCurrentSection);
      document.removeEventListener('keydown', wakeFromInteraction);
      document.removeEventListener('touchstart', wakeFromInteraction);
      if (sectionNavTimerRef.current) window.clearTimeout(sectionNavTimerRef.current);
    };
  }, [getCurrentCollectionSectionIndex, showCollectionSectionNav, wakeCollectionSectionNav]);

  useEffect(() => {
    setSectionNavState(prev => ({
      ...prev,
      currentIndex: Math.min(getCurrentCollectionSectionIndex(), Math.max(0, sections.length - 1)),
    }));
  }, [getCurrentCollectionSectionIndex, sections.length, sortField, sortDir, search]);


  function clearFilters() {
    setSearch('');
    setStatusFilter(new Set());
    setMethodFilter(new Set());
    setSubstrateFilter(new Set());
    setLocationFilter(new Set());
    setLightFilter(new Set());
    setGenusFilter(new Set());
  }

  if (loading) return (
    <div className="app">
      <header className="app-header">
        <div className="app-header-brand">
          <img src="/logo_only.svg" alt="Aroidex" className="app-header-logo" />
          <div>
            <h1>Aroidex</h1>
            <SkeletonLine width="4rem" height="0.65rem" style={{ marginTop: '0.2rem', background: 'rgba(255,255,255,0.15)', animation: 'none' }} />
          </div>
        </div>
        <div className="app-header-actions">
          <Link to="/new" className="btn btn-primary">+ Add Plant</Link>
        </div>
      </header>
      <div className="skeleton-search-bar">
        <SkeletonLine width="100%" height="2.3rem" style={{ borderRadius: '8px' }} />
      </div>
      <div className="skeleton-grid">
        {Array.from({ length: 8 }).map((_, i) => <SkeletonCard key={i} />)}
      </div>
    </div>
  );
  if (error) return <div className="app"><ErrorScreen message={error} onRetry={fetchPlants} /></div>;

  const renderCard = (plant) => (
    <Link to={`/plants/${plant.id}`} key={plant.id} className="plant-card-link" state={{ returnTo: '/' }} onClick={() => sessionStorage.setItem('plantListScroll', window.scrollY)}>
      <div className="plant-card plant-card-photo">
        {plant.latest_photo_id ? (
          <div className="card-photo">
            <img
              src={photoUrl(plant.latest_photo_id, plant.latest_photo_rotation_version, plant.latest_photo_created_at)}
              alt={plantDisplayName(plant) || plant.specimen_id}
              className="card-photo-img"
              loading="lazy"
              onLoad={e => e.currentTarget.classList.add('loaded')}
              style={{ objectPosition: `${plant.cover_photo_offset_x ?? 50}% ${plant.cover_photo_offset_y ?? 50}%`, transform: `scale(${plant.cover_photo_zoom ?? 1})`, transformOrigin: `${plant.cover_photo_offset_x ?? 50}% ${plant.cover_photo_offset_y ?? 50}%` }}
            />
            {plant.photo_count > 1 && (
              <span className="card-photo-count"><Icon name="camera" size={12} style={{ marginRight: 3 }} />{plant.photo_count}</span>
            )}
          </div>
        ) : (
          <div className="card-photo-empty">
            <span className="card-photo-empty-icon"><Icon name="sprout" size={28} /></span>
          </div>
        )}

        <div className="card-body">
          <div className="plant-card-header">
            <span className="specimen-id">{plant.specimen_id}</span>
            <span className={`status-badge status-${plant.status}`} style={statusStyle(plant.status, statusByKey)}>
              {formatStatus(plant.status, statusByKey)}
            </span>
          </div>

          <h2 className="plant-name">{plantDisplayName(plant) || plant.specimen_id}</h2>
          <p className="scientific-name">
            <em>{plant.genus} {plant.species}</em>
          </p>

          <div className="plant-details">
            {plant.growing_method && (
              <span className="detail-tag">{plant.growing_method}</span>
            )}
            {plant.substrate && plant.substrate !== plant.growing_method && (
              <span className="detail-tag">{formatSubstrate(plant.substrate)}</span>
            )}
            {plant.locations.map(loc => (
              <span key={loc.id} className="detail-tag location-tag">{loc.name}</span>
            ))}
            {plant.light_sources.map(ls => (
              <span key={ls.id} className="detail-tag light-tag">{ls.name}</span>
            ))}
          </div>

          {sortField === 'last_activity' && plant.last_activity && (
            <p className="plant-last-activity">
              Last activity: {formatDateTime(plant.last_activity)}
            </p>
          )}
          {sortField === 'created_at' && plant.created_at && (
            <p className="plant-last-activity">
              Added: {formatDateTime(plant.created_at)}
            </p>
          )}
          {sortField === 'acquisition_date' && plant.acquisition_date && (
            <p className="plant-last-activity">
              Acquired: {formatDate(plant.acquisition_date)}
            </p>
          )}

          {plant.cost != null && (
            <p className="plant-cost">${plant.cost.toFixed(2)}</p>
          )}
        </div>
      </div>
    </Link>
  );

  return (
    <div className="app">
      {refreshing && (
        <div className="plan-ptr-indicator">
          <Icon name="rotateCw" size={16} /> Refreshing…
        </div>
      )}
      <header className="app-header">
        <div className="app-header-brand">
          <img src="/logo_only.svg" alt="Aroidex" className="app-header-logo" />
          <div>
            <h1>Aroidex</h1>
            <p className="plant-count">{plants.length} plants</p>
          </div>
        </div>
        <div className="app-header-actions">
          <Link to="/new" className="btn btn-primary">+ Add Plant</Link>
        </div>
      </header>

      {/* -- Data Health banner — shown when ≥5 plants have missing priority fields */}
      {gapCount >= 5 && (
        bannerDismissed ? (
          healthDismissedVisible && (
            <div
              className="stale-banner-dismissed"
              onAnimationEnd={() => setHealthDismissedVisible(false)}
              style={{ animation: 'dismissedFade 2.5s ease-out forwards' }}
            >
              <Icon name="clipboardCheck" size={13} className="stale-banner-dismissed-icon" />
              <span>Data Health review dismissed</span>
            </div>
          )
        ) : (
          <div className="health-banner">
            <div className="health-banner-body">
              <Icon name="clipboardCheck" size={16} className="health-banner-icon" />
              <div>
                <p className="banner-title"><strong>{gapCount} plants</strong> have missing data</p>
                <p className="banner-sub">Location, light, and photos help you track plants better</p>
              </div>
            </div>
            <div className="health-banner-actions">
              <button className="btn btn-primary btn-sm" onClick={() => navigate('/health')}>
                Review →
              </button>
              <button className="btn btn-ghost btn-sm health-banner-dismiss" onClick={dismissBanner} aria-label="Dismiss" title="Dismiss">
                <Icon name="x" size={12} />
              </button>
            </div>
          </div>
        )
      )}

      {/* -- Care Round reminder banner — shown when plants exceed the observation staleness threshold */}
      {staleCount > 0 && (
        staleBannerDismissed ? (
          staleDismissedVisible && (
            <div
              className="stale-banner-dismissed"
              onAnimationEnd={() => setStaleDismissedVisible(false)}
              style={{ animation: 'dismissedFade 2.5s ease-out forwards' }}
            >
              <Icon name="waypoints" size={13} className="stale-banner-dismissed-icon" />
              <span>Care Round reminder dismissed for today · <Link to="/settings/notifications" className="stale-banner-dismissed-link">Notification settings</Link></span>
            </div>
          )
        ) : (
          <div className="stale-banner">
            <div className="stale-banner-body">
              <Icon name="waypoints" size={18} className="stale-banner-icon" />
              <div>
                <p className="banner-title"><strong>{staleCount} plant{staleCount !== 1 ? 's' : ''}</strong> due for a Care Round</p>
                <p className="banner-sub">Most overdue: {staleMaxDays} day{staleMaxDays !== 1 ? 's' : ''} since last observation</p>
              </div>
            </div>
            <div className="stale-banner-actions">
              <button className="btn btn-primary btn-sm" onClick={() => navigate('/rounds?stale=true')}>
                Start Round
              </button>
              <button className="btn btn-ghost btn-sm stale-banner-dismiss" onClick={dismissStaleBanner} aria-label="Dismiss" title="Dismiss">
                <Icon name="x" size={12} />
              </button>
            </div>
          </div>
        )
      )}

      <GuidanceSheet
        variant="fullscreen"
        isOpen={showWelcome && !loading && plants.length === 0 && !hasSeen('seen_welcome')}
        onDismiss={() => {
          sessionStorage.removeItem('aroidex_first_login');
          setShowWelcome(false);
        }}
        title={WELCOME_GUIDANCE_TITLE}
        dismissKey="seen_welcome"
        header={
          <div className="welcome-brand-header">
            <div className="welcome-brand-wordmark">
              <img src="/logo_only.svg" alt="" aria-hidden="true" className="welcome-brand-logo" />
              <span className="welcome-brand-name">Aroidex</span>
            </div>
            <div className="welcome-brand-icon" aria-hidden="true">
              <Icon name="leaf" size={64} />
            </div>
          </div>
        }
        primaryAction={{
          label: 'Add your first plant →',
          onClick: () => {
            sessionStorage.removeItem('aroidex_first_login');
            navigate('/new');
          },
        }}
        secondaryAction={{
          label: "I'll look around first",
          onClick: () => {
            sessionStorage.removeItem('aroidex_first_login');
            setShowWelcome(false);
          },
        }}
      >
        <WelcomeGuidanceBody />
      </GuidanceSheet>

      {plants.length === 0 ? (
        <div className="collection-empty-state">
          <div className="collection-empty-icon"><Icon name="sprout" size={34} /></div>
          <h2>Your collection is empty</h2>
          <p>Add your first plant to get started.</p>
          <button type="button" className="btn btn-primary" onClick={() => navigate('/new')}>
            Add a plant
          </button>
        </div>
      ) : (
        <>
      {/* -- Search + Sort + Filter toggle ---------------------------------- */}
      <div className="filter-bar">
        <div className="search-sort-row">
          <div className="search-input-wrap">
            <span className="search-icon" aria-hidden="true">
              <Icon name="search" size={14} />
            </span>
            <input
              type="text"
              className="search-input"
              placeholder="Search plants..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            {search && (
              <button
                type="button"
                className="search-clear-btn"
                onClick={() => setSearch('')}
                aria-label="Clear search"
                title="Clear search"
              >
                <Icon name="x" size={14} />
              </button>
            )}
          </div>

          {/* Sort controls */}
          <div className="sort-controls">
            <select
              className="sort-select"
              value={sortField}
              onChange={e => setSortField(e.target.value)}
            >
              {SORT_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            <button
              className="sort-dir-btn"
              onClick={() => setSortDir(d => d === 'asc' ? 'desc' : 'asc')}
              title={sortDir === 'asc' ? 'Ascending' : 'Descending'}
            >
              {sortDir === 'asc' ? <Icon name="arrowUp" size={14} /> : <Icon name="arrowDown" size={14} />}
            </button>
          </div>

          {/* Filter toggle */}
          <button
            className={`filter-toggle-btn ${filtersOpen ? 'filter-toggle-active' : ''}`}
            onClick={() => setFiltersOpen(o => !o)}
          >
            Filters
            {activeFilterCount > 0 && (
              <span className="filter-badge">{activeFilterCount}</span>
            )}
            <span className="filter-toggle-arrow">{filtersOpen ? <Icon name="chevronUp" size={13} /> : <Icon name="chevronDown" size={13} />}</span>
          </button>
        </div>

        {/* -- Expanded filter panel ---------------------------------------- */}
        {filtersOpen && (
          <div className="filter-panel">

            <div className="filter-group-block">
              <span className="filter-label">Status</span>
              <div className="filter-chips">
                {statuses.map(s => (
                  <button
                    key={s}
                    className={`filter-chip ${statusFilter.has(s) ? 'filter-chip-active' : ''}`}
                    onClick={() => toggleSet(setStatusFilter, s)}
                  >{formatStatus(s, statusByKey)}</button>
                ))}
              </div>
            </div>

            <div className="filter-group-block">
              <span className="filter-label">Growing Method</span>
              <div className="filter-chips">
                {methods.map(m => (
                  <button
                    key={m}
                    className={`filter-chip ${methodFilter.has(m) ? 'filter-chip-active' : ''}`}
                    onClick={() => toggleSet(setMethodFilter, m)}
                  >{m}</button>
                ))}
              </div>
            </div>

            {visibleSubstrateGroups.length > 0 && (
              <div className="filter-group-block">
                <span className="filter-label">Substrate</span>
                <div className="filter-substrate-groups">
                  {visibleSubstrateGroups.map(({ method, substrates }) => (
                    <div
                      key={method}
                      className="filter-substrate-group"
                      style={{ '--method-color': methodColorMap.get(method) }}
                    >
                      <span className="filter-substrate-method-label">{method}</span>
                      <div className="filter-chips">
                        {substrates.map(s => (
                          <button
                            key={s}
                            className={`filter-chip filter-chip--method ${substrateFilter.has(s) ? 'filter-chip-active filter-chip-active--method' : ''}`}
                            onClick={() => toggleSet(setSubstrateFilter, s)}
                          >{s}</button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="filter-group-block">
              <span className="filter-label">Location</span>
              <div className="filter-chips">
                {locations.map(l => (
                  <button
                    key={l.id}
                    className={`filter-chip ${locationFilter.has(l.id) ? 'filter-chip-active' : ''}`}
                    onClick={() => toggleSet(setLocationFilter, l.id)}
                  >{l.name}</button>
                ))}
              </div>
            </div>

            <div className="filter-group-block">
              <span className="filter-label">Light Source</span>
              <div className="filter-chips">
                {lightSources.map(l => (
                  <button
                    key={l.id}
                    className={`filter-chip ${lightFilter.has(l.id) ? 'filter-chip-active' : ''}`}
                    onClick={() => toggleSet(setLightFilter, l.id)}
                  >{l.name}</button>
                ))}
              </div>
            </div>

            <div className="filter-group-block">
              <span className="filter-label">Genus</span>
              <div className="filter-chips">
                {genera.map(g => (
                  <button
                    key={g}
                    className={`filter-chip ${genusFilter.has(g) ? 'filter-chip-active' : ''}`}
                    onClick={() => toggleSet(setGenusFilter, g)}
                  >{g}</button>
                ))}
              </div>
            </div>

            {activeFilterCount > 0 && (
              <button className="clear-filters" onClick={clearFilters}>
                Clear all filters
              </button>
            )}
          </div>
        )}
      </div>

      {/* -- Results --------------------------------------------------------- */}
      {filteredPlants.length === 0 ? (
        <p className="empty-state" style={{ marginTop: '2rem', textAlign: 'center' }}>
          No plants match your search
        </p>
      ) : (
        <>
          {hasActiveFilters && (
            <p className="filter-count">Showing {filteredPlants.length} of {plants.length}</p>
          )}
          {sections.map(({ key, plants: sp }) => (
            <div key={key} className="section-group" data-collection-section={key}>
              <div className="scroll-section-header">
                {usesGenusCollectionSections(sortField) ? <em>{key}</em> : key}
              </div>
              <div className="plant-grid">
                {sp.map(renderCard)}
              </div>
            </div>
          ))}
        </>
      )}
        </>
      )}

      {showCollectionSectionNav && createPortal(
        <div
          className={`explore-genus-jump-rail collection-section-jump-rail ${sectionNavState.active ? 'collection-section-jump-rail--active' : ''}`}
          aria-label="Section navigation"
        >
          {sectionNavState.currentIndex > 0 && (
            <button
              type="button"
              className="explore-genus-jump explore-genus-jump--prev"
              onClick={() => jumpCollectionSection(-1)}
              onPointerEnter={wakeCollectionSectionNav}
              aria-label="Previous section"
              title="Previous section"
            >
              <Icon name="chevronUp" size={18} />
            </button>
          )}
          {sectionNavState.currentIndex < sections.length - 1 && (
            <button
              type="button"
              className="explore-genus-jump explore-genus-jump--next"
              onClick={() => jumpCollectionSection(1)}
              onPointerEnter={wakeCollectionSectionNav}
              aria-label="Next section"
              title="Next section"
            >
              <Icon name="chevronDown" size={18} />
            </button>
          )}
        </div>,
        document.body
      )}
    </div>
  );
}

function Archive() {
  const [plants, setPlants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const { byKey: statusByKey } = useStatusMeta();
  const { formatDate } = useDateFormat();

  useEffect(() => {
    let cancelled = false;
    apiFetch('/plants/archive')
      .then(res => res.ok ? res.json() : Promise.reject(new Error('Failed to fetch archived plants')))
      .then(data => {
        if (!cancelled) {
          setPlants(data);
          setLoading(false);
        }
      })
      .catch(err => {
        if (!cancelled) {
          setError(err.message);
          setLoading(false);
        }
      });
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div className="app">
        <header className="sub-header">
          <button className="sub-header-back" onClick={() => navigate('/', { replace: true })} aria-label="Back" title="Back">
            <Icon name="arrowLeft" size={20} />
          </button>
          <div className="sub-header-title">
            <h1>Archive</h1>
          </div>
        </header>
        <div className="plant-list">
          <SkeletonCard /><SkeletonCard /><SkeletonCard />
        </div>
      </div>
    );
  }

  if (error) return <ErrorScreen title="Archive unavailable" message={error} />;

  return (
    <div className="app">
      <header className="sub-header">
        <button className="sub-header-back" onClick={() => navigate('/', { replace: true })} aria-label="Back" title="Back">
          <Icon name="arrowLeft" size={20} />
        </button>
        <div className="sub-header-title">
          <h1>Archive</h1>
        </div>
      </header>

      {plants.length === 0 ? (
        <div className="empty-state"><p>No archived plants yet.</p></div>
      ) : (
        <div className="plant-grid">
          {plants.map(plant => (
            <Link key={plant.id} to={`/plants/${plant.id}`} state={{ returnTo: '/archive' }} className="plant-card-link">
              <div className="plant-card plant-card-photo">
                {plant.latest_photo_id ? (
                  <div className="card-photo">
                    <img
                      src={photoUrl(plant.latest_photo_id, plant.latest_photo_rotation_version, plant.latest_photo_created_at)}
                      alt={plantDisplayName(plant) || plant.specimen_id}
                      className="card-photo-img"
                      loading="lazy"
                      onLoad={e => e.currentTarget.classList.add('loaded')}
                      style={{ objectPosition: `${plant.cover_photo_offset_x ?? 50}% ${plant.cover_photo_offset_y ?? 50}%`, transform: `scale(${plant.cover_photo_zoom ?? 1})`, transformOrigin: `${plant.cover_photo_offset_x ?? 50}% ${plant.cover_photo_offset_y ?? 50}%` }}
                    />
                  </div>
                ) : (
                  <div className="card-photo-empty">
                    <span className="card-photo-empty-icon"><Icon name="sprout" size={28} /></span>
                  </div>
                )}
                <div className="card-body">
                  <div className="plant-card-header">
                    <span className="specimen-id">{plant.specimen_id}</span>
                    <span className={`status-badge status-${plant.status}`} style={statusStyle(plant.status, statusByKey)}>
                      {formatStatus(plant.status, statusByKey)}
                    </span>
                  </div>
                  <h2 className="plant-name">{plantDisplayName(plant) || plant.specimen_id}</h2>
                  <p className="scientific-name"><em>{plant.genus} {plant.species}</em></p>
                  {plant.archived_date && <p className="plant-last-activity">Archived: {formatDate(plant.archived_date)}</p>}
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Nav Icons ─────────────────────────────────────────────────────────────

const NAV_ICONS = {
  collection: (
    <svg viewBox="0 0 24 24" fill="none" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="7" height="7" rx="1.5"/>
      <rect x="14" y="3" width="7" height="7" rx="1.5"/>
      <rect x="3" y="14" width="7" height="7" rx="1.5"/>
      <rect x="14" y="14" width="7" height="7" rx="1.5"/>
    </svg>
  ),
  rounds: (
    <svg viewBox="0 0 24 24" fill="none" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <path d="m10.586 5.414-5.172 5.172"/>
      <path d="m18.586 13.414-5.172 5.172"/>
      <path d="M6 12h12"/>
      <circle cx="12" cy="20" r="2"/>
      <circle cx="12" cy="4" r="2"/>
      <circle cx="20" cy="12" r="2"/>
      <circle cx="4" cy="12" r="2"/>
    </svg>
  ),
  plan: (
    <svg viewBox="0 0 24 24" fill="none" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="2"/>
      <line x1="16" y1="2" x2="16" y2="6"/>
      <line x1="8" y1="2" x2="8" y2="6"/>
      <line x1="3" y1="10" x2="21" y2="10"/>
    </svg>
  ),
  add: (
    <svg viewBox="0 0 24 24" fill="none" strokeWidth="1.75" strokeLinecap="round">
      <circle cx="12" cy="12" r="9"/>
      <line x1="12" y1="8" x2="12" y2="16"/>
      <line x1="8" y1="12" x2="16" y2="12"/>
    </svg>
  ),
  stats: (
    <svg viewBox="0 0 24 24" fill="none" strokeWidth="1.75" strokeLinecap="round">
      <line x1="18" y1="20" x2="18" y2="10"/>
      <line x1="12" y1="20" x2="12" y2="4"/>
      <line x1="6" y1="20" x2="6" y2="14"/>
    </svg>
  ),
  wishlist: (
    <svg viewBox="0 0 24 24" fill="none" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
    </svg>
  ),
  activity: (
    <svg viewBox="0 0 24 24" fill="none" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/>
      <path d="M3 3v5h5"/>
      <path d="M12 7v5l4 2"/>
    </svg>
  ),
  archive: (
    <svg viewBox="0 0 24 24" fill="none" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="4" rx="1"/>
      <path d="M5 8v11a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8"/>
      <path d="M10 12h4"/>
    </svg>
  ),
  settings: (
    <Icon name="settings" size={24} strokeWidth={1.75} />
  ),
  more: (
    <svg viewBox="0 0 24 24" fill="none" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="1"/>
      <circle cx="19" cy="12" r="1"/>
      <circle cx="5" cy="12" r="1"/>
    </svg>
  ),
  explore: (
    <svg viewBox="0 0 24 24" fill="none" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <g transform="matrix(0.877,0,0,0.935,0.895,-0.218)" strokeWidth="1.988">
        <path d="m22.09 12.736c.048-.384.073-.774.073-1.17 0-3.82-1-6-2-8-2 2.48-3.5 3-9.2 4.1-8.463 1.447-7.388 13.923 1.198 13.9.148 0 .296-.005.443-.013"/>
        <path d="m3.163 22.566c0-3 1.85-5.36 5.08-6 2.42-.48 4.92-2 5.92-3"/>
      </g>
      <g transform="translate(-0.643,-0.925)">
        <circle cx="18" cy="18" r="3"/>
        <path d="M22 22 20.1 20.1"/>
      </g>
    </svg>
  ),
};

const NAV_LABELS = {
  collection: 'Collection',
  add:        'Add',
  stats:      'Stats',
  wishlist:   'Wishlist',
  activity:   'Activity',
  archive:    'Archive',
  rounds:     'Rounds',
  plan:       'Plan',
  settings:   'Settings',
  more:       'More',
  explore:    'Explore',
};

const MORE_ITEMS = [
  { key: 'stats',       label: 'Stats',               path: '/stats',       icon: NAV_ICONS.stats },
  { key: 'wishlist',    label: 'Wishlist',             path: '/wishlist',    icon: NAV_ICONS.wishlist },
  { key: 'archive',     label: 'Archive',              path: '/archive',     icon: NAV_ICONS.archive },
  { key: 'activity',   label: 'Recent Activity',      path: '/activity',    icon: NAV_ICONS.activity },
  {
    key: 'propagation', label: 'Propagation Bay', path: '/propagation',
    icon: <Icon name="sprout" size={24} strokeWidth={1.75} />,
  },
  {
    key: 'health', label: 'Data Health', path: '/health',
    icon: <svg viewBox="0 0 24 24" fill="none" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><rect x="8" y="2" width="8" height="4" rx="1"/><path d="M9 4H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2h-3"/><path d="m9 14 2 2 4-4"/></svg>,
  },
  { key: 'settings',   label: 'Settings',             path: '/settings',    icon: NAV_ICONS.settings },
];

// ─── Bottom Nav Bar ────────────────────────────────────────────────────────

const FIXED_NAV_SLOTS = ['collection', 'rounds', 'plan', 'add', 'explore', 'more'];

function BottomNav({ onClearCollection }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { session } = useAuth();
  const [moreOpen, setMoreOpen] = useState(false);
  const [dataHealthHelpOpen, setDataHealthHelpOpen] = useState(false);
  const [roundsHelpOpen, setRoundsHelpOpen] = useState(false);
  const [planHelpOpen, setPlanHelpOpen] = useState(false);
  const sheetRef  = useRef(null);
  const dragState = useRef(null); // { startY, startTime } while dragging
  const [activePropCount, setActivePropCount] = useState(0);

  useEffect(() => {
    fetch('/propagation-items/count')
      .then(r => r.ok ? r.json() : { active: 0 })
      .then(d => setActivePropCount(d.active || 0))
      .catch(() => {});
  }, [location.pathname]);

  const moreItems = useMemo(() => {
    const items = MORE_ITEMS
      .filter(item => item.key !== 'propagation' || activePropCount > 0)
      .map(item => item.key === 'propagation' && activePropCount > 0
        ? { ...item, badge: activePropCount }
        : item
      );
    if (session?.user?.is_superadmin) {
      const settingsIdx = items.findIndex(i => i.key === 'settings');
      const adminItem = {
        key: 'admin', label: 'Admin Console', path: '/admin',
        icon: <svg viewBox="0 0 24 24" fill="none" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>,
      };
      if (settingsIdx >= 0) items.splice(settingsIdx, 0, adminItem);
      else items.push(adminItem);
    }
    return items;
  }, [session, activePropCount]);

  const activeKey = useMemo(() => {
    const path = location.pathname;
    if (path === '/')                    return 'collection';
    if (path.startsWith('/new'))         return 'add';
    if (path === '/rounds')              return 'rounds';
    if (path === '/plan')                return 'plan';
    if (path === '/explore')             return 'explore';
    if (path.startsWith('/explore/'))    return 'explore';
    if (path === '/compare')             return 'explore';
    if (path === '/stats')               return 'more';
    if (path === '/wishlist')            return 'more';
    if (path === '/archive')             return 'more';
    if (path === '/activity')            return 'more';
    if (path === '/propagation')         return 'more';
    if (path === '/health')              return 'more';
    if (path.startsWith('/settings'))    return 'more';
    if (path.startsWith('/admin'))       return 'more';
    return 'collection';
  }, [location.pathname]);

  const hideNav = useMemo(() => {
    const path = location.pathname;
    return (
      path.includes('/plants/')       ||
      path === '/new/manual'          ||
      path === '/qa'                  ||
      path === '/compare'             ||
      path.startsWith('/settings/')   ||
      path.startsWith('/explore/')
    );
  }, [location.pathname]);

  // Close sheet on route change
  const prevPath = useRef(location.pathname);
  useEffect(() => {
    if (location.pathname !== prevPath.current) {
      prevPath.current = location.pathname;
      setMoreOpen(false);
    }
  }, [location.pathname]);

  if (hideNav) return null;

  const scrollCurrentTabToTop = () => {
    if (window.scrollY >= 10) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return true;
    }
    return false;
  };

  const handleNavClick = (key) => {
    if (key === 'more') {
      setMoreOpen(o => !o);
      return;
    }
    setMoreOpen(false);
    if (key === 'collection') {
      if (activeKey !== 'collection') { navigate('/'); return; }
      const saved = (() => {
        try { return JSON.parse(sessionStorage.getItem('plantListState') || '{}'); } catch { return {}; }
      })();
      const hasFilters =
        saved.search ||
        (saved.statusFilter?.length    ?? 0) > 0 ||
        (saved.methodFilter?.length    ?? 0) > 0 ||
        (saved.substrateFilter?.length ?? 0) > 0 ||
        (saved.locationFilter?.length  ?? 0) > 0 ||
        (saved.lightFilter?.length     ?? 0) > 0 ||
        (saved.genusFilter?.length     ?? 0) > 0;
      if (scrollCurrentTabToTop()) return;
      if (hasFilters) onClearCollection();
    } else if (key === activeKey) {
      scrollCurrentTabToTop();
    } else if (key === 'add')     { navigate('/new'); }
      else if (key === 'rounds')  { navigate('/rounds'); }
      else if (key === 'plan')    { navigate('/plan'); }
      else if (key === 'explore') { navigate('/explore'); }
  };

  function onDragStart(e) {
    dragState.current = { startY: e.touches[0].clientY, startTime: Date.now() };
    if (sheetRef.current) sheetRef.current.style.transition = 'none';
  }

  function onDragMove(e) {
    if (!dragState.current || !sheetRef.current) return;
    const dy = e.touches[0].clientY - dragState.current.startY;
    if (dy < 0) return; // don't allow dragging up past resting position
    sheetRef.current.style.transform = `translateY(${dy}px)`;
  }

  function onDragEnd(e) {
    if (!dragState.current || !sheetRef.current) return;
    const dy       = e.changedTouches[0].clientY - dragState.current.startY;
    const elapsed  = Date.now() - dragState.current.startTime;
    const velocity = dy / Math.max(elapsed, 1); // px/ms
    dragState.current = null;

    if (dy > 80 || velocity > 0.5) {
      setMoreOpen(false);
    } else {
      // Snap back to resting position
      sheetRef.current.style.transition = 'transform 220ms cubic-bezier(0.32, 0.72, 0, 1)';
      sheetRef.current.style.transform  = 'translateY(0)';
    }
  }

  return (
    <>
      {moreOpen && (
        <div className="more-sheet-scrim" onClick={() => setMoreOpen(false)} />
      )}
      {moreOpen && (
        <div
          className="more-sheet"
          role="dialog"
          aria-label="More navigation"
          ref={sheetRef}
          onTouchStart={onDragStart}
          onTouchMove={onDragMove}
          onTouchEnd={onDragEnd}
        >
          <div className="more-sheet-handle" />
          <nav className="more-sheet-nav">
            {moreItems.map(item => {
              const isActive = location.pathname === item.path ||
                (item.path === '/settings' && location.pathname.startsWith('/settings'));
              return (
                <Link
                  key={item.key}
                  to={item.path}
                  className={`more-sheet-item${isActive ? ' more-sheet-item--active' : ''}`}
                  onClick={() => setMoreOpen(false)}
                >
                  <span className="more-sheet-item-icon">{item.icon}</span>
                  <span className="more-sheet-item-label">{item.label}</span>
                  {item.badge > 0 && (
                    <span className="nav-badge">{item.badge}</span>
                  )}
                </Link>
              );
            })}
            <div className="more-sheet-divider" />
            <div className="more-sheet-section-label">Help</div>
            <button
              type="button"
              className="more-sheet-item more-sheet-help-item more-sheet-help-item--secondary"
              onClick={() => {
                setMoreOpen(false);
                setDataHealthHelpOpen(true);
              }}
            >
              <span className="more-sheet-item-icon"><Icon name="helpCircle" size={18} /></span>
              <span className="more-sheet-item-label">How Data Health works</span>
            </button>
            <button
              type="button"
              className="more-sheet-item more-sheet-help-item more-sheet-help-item--secondary"
              onClick={() => {
                setMoreOpen(false);
                setRoundsHelpOpen(true);
              }}
            >
              <span className="more-sheet-item-icon"><Icon name="helpCircle" size={18} /></span>
              <span className="more-sheet-item-label">How Care Rounds works</span>
            </button>
            <button
              type="button"
              className="more-sheet-item more-sheet-help-item more-sheet-help-item--secondary"
              onClick={() => {
                setMoreOpen(false);
                setPlanHelpOpen(true);
              }}
            >
              <span className="more-sheet-item-icon"><Icon name="helpCircle" size={18} /></span>
              <span className="more-sheet-item-label">How Care Plan works</span>
            </button>
          </nav>
        </div>
      )}
      <GuidanceSheet
        variant="sheet"
        isOpen={dataHealthHelpOpen}
        onDismiss={() => setDataHealthHelpOpen(false)}
        title={DATA_HEALTH_GUIDANCE_TITLE}
        primaryAction={{ label: 'Got it', onClick: () => setDataHealthHelpOpen(false) }}
      >
        <DataHealthGuidanceBody />
      </GuidanceSheet>
      <GuidanceSheet
        variant="sheet"
        isOpen={roundsHelpOpen}
        onDismiss={() => setRoundsHelpOpen(false)}
        title={ROUNDS_GUIDANCE_TITLE}
        primaryAction={{ label: 'Got it', onClick: () => setRoundsHelpOpen(false) }}
      >
        <RoundsGuidanceBody />
      </GuidanceSheet>
      <GuidanceSheet
        variant="sheet"
        isOpen={planHelpOpen}
        onDismiss={() => setPlanHelpOpen(false)}
        title={PLAN_GUIDANCE_TITLE}
        primaryAction={{ label: 'Got it', onClick: () => setPlanHelpOpen(false) }}
      >
        <PlanGuidanceBody />
      </GuidanceSheet>
      <nav className="bottom-nav">
        {FIXED_NAV_SLOTS.map(key => {
          const active = activeKey === key;
          return (
            <button
              key={key}
              className={`bottom-nav-btn ${active ? 'bottom-nav-btn--active' : ''}`}
              onClick={() => handleNavClick(key)}
              aria-label={NAV_LABELS[key]}
            >
              <span className="bottom-nav-icon">{NAV_ICONS[key]}</span>
              <span className="bottom-nav-label">{NAV_LABELS[key]}</span>
            </button>
          );
        })}
      </nav>
    </>
  );
}

// ─── Route Transition ──────────────────────────────────────────────────────────
// Wraps <Routes> and applies a directional animation class on each navigation.
// Direction is inferred from path depth: deeper = forward, shallower = back, tab root = fade.

const TAB_PATHS = new Set(['/', '/new', '/stats', '/wishlist', '/archive', '/activity', '/plan', '/settings', '/explore', '/admin']);

function RouteTransition({ children }) {
  const location = useLocation();
  const [animKey,  setAnimKey]  = useState(0);
  const [animClass, setAnimClass] = useState('');
  const prevPathRef = useRef(location.pathname);

  useEffect(() => {
    const prev = prevPathRef.current;
    const next = location.pathname;
    prevPathRef.current = next;

    if (prev === next) return;

    let cls;
    if (TAB_PATHS.has(next) && TAB_PATHS.has(prev)) {
      // Both endpoints are top-level tab roots — fade, don't slide
      cls = 'route-transition--tab';
    } else if (next.split('/').length < prev.split('/').length) {
      // Going shallower in the hierarchy (back)
      cls = 'route-transition--back';
    } else {
      // Going deeper (forward)
      cls = 'route-transition--forward';
    }

    setAnimClass(cls);
    setAnimKey(k => k + 1);
  }, [location.pathname]);

  return (
    <div key={animKey} className={`route-transition ${animClass}`}>
      {children}
    </div>
  );
}

// ─── Auth Flow ─────────────────────────────────────────────────────────────

const BETA_SIGNAL_TEXT = 'Aroidex is in early beta. Things may change.';

const INVITE_ERROR_MESSAGES = {
  invite_used: 'This invite has already been used.',
  invite_revoked: 'This invite has been revoked — contact the person who sent it for a new one.',
  invite_not_found: 'Invite code not found — check for typos.',
  invite_expired: 'This invite has expired — ask for a new one.',
};

function inviteTerminology(message) {
  return message.replace(/join\s+code/gi, 'invite code');
}

function signupInviteError(message, hasInviteCode) {
  if (INVITE_ERROR_MESSAGES[message]) return INVITE_ERROR_MESSAGES[message];
  if (hasInviteCode && /^Request failed \(400\)$/.test(message)) {
    return INVITE_ERROR_MESSAGES.invite_not_found;
  }
  return '';
}

// Password input with show/hide toggle.
// The toggle is only shown once the field has content — this avoids the
// focus/blur timing race that occurs with focus-only visibility (clicking the
// button blurs the input first, hiding the button before the click lands).
function PasswordInput(props) {
  const [show, setShow] = useState(false);
  const hasValue = Boolean(props.value?.length);
  return (
    <div className="password-field-wrap">
      <input {...props} type={show ? 'text' : 'password'} />
      {hasValue && (
        <button
          type="button"
          className="password-toggle-btn"
          onClick={() => setShow(s => !s)}
          aria-label={show ? 'Hide password' : 'Show password'}
          title={show ? 'Hide password' : 'Show password'}
        >
          <Icon name={show ? 'eyeOff' : 'eye'} size={18} />
        </button>
      )}
    </div>
  );
}

const AuthContext = createContext(null);

export function useAuth() {
  return useContext(AuthContext);
}

function AuthScreen({ onAuthenticated }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [mode, setMode] = useState('login');
  const [form, setForm] = useState({
    email: '',
    password: '',
    display_name: '',
    household_name: '',
    join_code: '',
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [inviteCodeError, setInviteCodeError] = useState('');
  const [forgotSent, setForgotSent] = useState(false);
  // signup_mode fetched from /auth/config on mount; default 'closed' keeps
  // the UI safe while the fetch is in flight or if it fails.
  const [signupModeConfig, setSignupModeConfig] = useState('closed');
  // open-mode picker: 'create' shows household name input, 'join' shows invite code
  const [signupPath, setSignupPath] = useState('create');
  const [prefilledCode, setPrefilledCode] = useState('');

  const signupMode = mode === 'signup';
  const forgotMode = mode === 'forgot';

  // Fetch signup config once on mount so the signup form renders correctly.
  useEffect(() => {
    fetch('/auth/config')
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data?.signup_mode) setSignupModeConfig(data.signup_mode); })
      .catch(() => {}); // silently fall back to 'closed' on network failure
  }, []);

  useEffect(() => {
    if (location.pathname === '/signup') {
      setMode('signup');
    }
    const code = new URLSearchParams(location.search).get('code');
    if (!code) return;
    setPrefilledCode(code);
    setMode('signup');
    setSignupPath('join');
    setForm(prev => ({ ...prev, join_code: code, household_name: '' }));
  }, [location.pathname, location.search]);

  function updateField(key, value) {
    setForm(prev => ({ ...prev, [key]: value }));
    if (key === 'join_code') setInviteCodeError('');
  }

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    setError('');
    setInviteCodeError('');

    if (forgotMode) {
      try {
        await apiFetch('/auth/password-reset/request', {
          method: 'POST',
          body: JSON.stringify({ email: form.email }),
        });
        setForgotSent(true);
      } catch {
        // Always show success to prevent email enumeration
        setForgotSent(true);
      } finally {
        setBusy(false);
      }
      return;
    }

    let payload;
    if (signupMode) {
      const base = {
        email: form.email,
        password: form.password,
        display_name: form.display_name || null,
      };
      if (signupModeConfig === 'open') {
        // open mode: send only the relevant field — backend rejects both/neither
        if (signupPath === 'join' && !form.join_code.trim()) {
          setInviteCodeError('Invite code is required');
          setBusy(false);
          return;
        }
        payload = signupPath === 'create'
          ? { ...base, household_name: form.household_name || 'My Household' }
          : { ...base, join_code: form.join_code || null };
      } else {
        // closed mode: invite code required; household_name covers the bootstrap case
        payload = { ...base, household_name: form.household_name || 'My Household', join_code: form.join_code || null };
      }
    } else {
      payload = { email: form.email, password: form.password };
    }

    try {
      const res = await apiFetch(signupMode ? '/auth/signup' : '/auth/login', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(await readApiError(res));
      if (signupMode) sessionStorage.setItem('aroidex_first_login', '1');
      onAuthenticated(await res.json());
      navigate('/', { replace: true });
    } catch (err) {
      const mappedInviteError = signupInviteError(err.message, Boolean(form.join_code.trim()));
      if (signupMode && mappedInviteError) {
        setInviteCodeError(mappedInviteError);
      } else {
        setError(inviteTerminology(err.message));
      }
    } finally {
      setBusy(false);
    }
  }

  if (forgotMode) {
    return (
      <main className="auth-screen">
        <section className="auth-panel">
          <div className="auth-brand">
            <img src="/logo_only.svg" alt="Aroidex" className="auth-logo" />
            <div>
              <h1>Aroidex</h1>
              <p>Reset your password</p>
            </div>
          </div>

          {forgotSent ? (
            <div className="auth-form">
              <p className="auth-reset-sent">
                If an account exists for that email address, a reset link has been sent. Check your inbox.
              </p>
              <button
                type="button"
                className="btn btn-primary auth-submit"
                onClick={() => { setMode('login'); setForgotSent(false); setError(''); }}
              >
                Back to Log In
              </button>
            </div>
          ) : (
            <form className="auth-form" onSubmit={submit}>
              <label className="form-field">
                <span className="form-label">Email</span>
                <input
                  type="email"
                  className="form-input"
                  value={form.email}
                  onChange={e => updateField('email', e.target.value)}
                  autoComplete="email"
                  autoCorrect="off"
                  autoCapitalize="off"
                  required
                />
              </label>
              {error && <p className="auth-error">{error}</p>}
              <button type="submit" className="btn btn-primary auth-submit" disabled={busy}>
                {busy ? 'Sending...' : 'Send Reset Link'}
              </button>
              <button
                type="button"
                className="auth-forgot-link"
                onClick={() => { setMode('login'); setError(''); }}
              >
                Back to Log In
              </button>
            </form>
          )}
        </section>
      </main>
    );
  }

  return (
    <main className="auth-screen">
      <section className="auth-panel">
        <div className="auth-brand">
          <img src="/logo_only.svg" alt="Aroidex" className="auth-logo" />
          <div>
            <h1>Aroidex</h1>
            <p>{signupMode ? 'Create your beta account' : 'Sign in to your collection'}</p>
          </div>
        </div>

        <div className="settings-segment auth-mode-switch">
          <button
            type="button"
            className={`settings-segment-btn ${!signupMode ? 'settings-segment-btn--active' : ''}`}
            onClick={() => { setMode('login'); setError(''); }}
          >
            Log In
          </button>
          <button
            type="button"
            className={`settings-segment-btn ${signupMode ? 'settings-segment-btn--active' : ''}`}
            onClick={() => { setMode('signup'); setError(''); }}
          >
            Sign Up
          </button>
        </div>

        <form className="auth-form" onSubmit={submit}>
          {signupMode && signupModeConfig === 'open' && !prefilledCode && (
            <>
              <p className="auth-beta-signal">{BETA_SIGNAL_TEXT}</p>
              <div className="auth-choice-grid" aria-label="Signup path">
                <button
                  type="button"
                  className={`auth-choice-card ${signupPath === 'create' ? 'auth-choice-card--active' : ''}`}
                  onClick={() => { setSignupPath('create'); updateField('join_code', ''); }}
                >
                  Create my own household
                </button>
                <button
                  type="button"
                  className={`auth-choice-card ${signupPath === 'join' ? 'auth-choice-card--active' : ''}`}
                  onClick={() => { setSignupPath('join'); updateField('household_name', ''); }}
                >
                  Join an existing household
                </button>
              </div>
            </>
          )}

          <label className="form-field">
            <span className="form-label">Email</span>
            <input
              type="email"
              className="form-input"
              value={form.email}
              onChange={e => updateField('email', e.target.value)}
              autoComplete="email"
              autoCorrect="off"
              autoCapitalize="off"
              required
            />
          </label>

          <label className="form-field">
            <span className="form-label">Password</span>
            <PasswordInput
              className="form-input"
              value={form.password}
              onChange={e => updateField('password', e.target.value)}
              autoComplete={signupMode ? 'new-password' : 'current-password'}
              minLength={8}
              required
            />
          </label>

          {signupMode && (
            <>
              <label className="form-field">
                <span className="form-label">Display Name</span>
                <input
                  type="text"
                  className="form-input"
                  value={form.display_name}
                  onChange={e => updateField('display_name', e.target.value)}
                  autoComplete="name"
                />
              </label>

              {/* open-mode create: household name input */}
              {signupModeConfig === 'open' && signupPath === 'create' && (
                <label className="form-field">
                  <span className="form-label">Collection Name</span>
                  <input
                    type="text"
                    className="form-input"
                    value={form.household_name}
                    onChange={e => updateField('household_name', e.target.value)}
                    autoComplete="off"
                    required
                  />
                </label>
              )}

              {/* closed mode, prefilled invite link, or open-mode join: invite code input */}
              {(signupModeConfig === 'closed' || prefilledCode || signupPath === 'join') && (
                <label className="form-field">
                  <span className="form-label">Invite Code</span>
                  <input
                    type="text"
                    className="form-input auth-code-input"
                    value={form.join_code}
                    onChange={e => updateField('join_code', e.target.value)}
                    autoCapitalize="characters"
                    autoComplete="off"
                    required={signupModeConfig === 'open' && (prefilledCode || signupPath === 'join')}
                  />
                  {inviteCodeError && <span className="form-field-error">{inviteCodeError}</span>}
                </label>
              )}
            </>
          )}

          {error && <p className="auth-error">{error}</p>}

          <button type="submit" className="btn btn-primary auth-submit" disabled={busy}>
            {busy ? 'Working...' : (signupMode ? 'Create Account' : 'Log In')}
          </button>

          {!signupMode && (
            <button
              type="button"
              className="auth-forgot-link"
              onClick={() => { setMode('forgot'); setError(''); }}
            >
              Forgot password?
            </button>
          )}
        </form>
      </section>
    </main>
  );
}

function ResetPasswordScreen({ onAuthenticated }) {
  const navigate = useNavigate();
  const location = useLocation();
  const token = new URLSearchParams(location.search).get('token') || '';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  async function submit(e) {
    e.preventDefault();
    if (password !== confirm) { setError('Passwords do not match.'); return; }
    setBusy(true);
    setError('');
    try {
      const res = await apiFetch('/auth/password-reset/confirm', {
        method: 'POST',
        body: JSON.stringify({ token, password }),
      });
      if (!res.ok) throw new Error(await readApiError(res));
      onAuthenticated(await res.json());
      navigate('/', { replace: true });
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  if (!token) {
    return (
      <main className="auth-screen">
        <section className="auth-panel">
          <div className="auth-brand">
            <img src="/logo_only.svg" alt="Aroidex" className="auth-logo" />
            <div><h1>Aroidex</h1><p>Reset your password</p></div>
          </div>
          <div className="auth-form">
            <p className="auth-error">Invalid or missing reset link.</p>
            <button
              type="button"
              className="btn btn-primary auth-submit"
              onClick={() => navigate('/', { replace: true })}
            >
              Back to Log In
            </button>
          </div>
        </section>
      </main>
    );
  }

  return (
    <main className="auth-screen">
      <section className="auth-panel">
        <div className="auth-brand">
          <img src="/logo_only.svg" alt="Aroidex" className="auth-logo" />
          <div><h1>Aroidex</h1><p>Set a new password</p></div>
        </div>
        <form className="auth-form" onSubmit={submit}>
          <label className="form-field">
            <span className="form-label">New Password</span>
            <PasswordInput
              className="form-input"
              value={password}
              onChange={e => setPassword(e.target.value)}
              autoComplete="new-password"
              minLength={8}
              required
            />
          </label>
          <label className="form-field">
            <span className="form-label">Confirm Password</span>
            <PasswordInput
              className="form-input"
              value={confirm}
              onChange={e => setConfirm(e.target.value)}
              autoComplete="new-password"
              minLength={8}
              required
            />
          </label>
          {error && <p className="auth-error">{error}</p>}
          <button type="submit" className="btn btn-primary auth-submit" disabled={busy}>
            {busy ? 'Saving...' : 'Set New Password'}
          </button>
        </form>
      </section>
    </main>
  );
}

function VerifyEmailScreen() {
  const location = useLocation();
  const navigate = useNavigate();
  const token = new URLSearchParams(location.search).get('token') || '';
  const [status, setStatus] = useState('loading'); // 'loading' | 'success' | 'error'
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (!token) { setStatus('error'); setMessage('Invalid verification link.'); return; }
    apiFetch(`/auth/verify-email?token=${encodeURIComponent(token)}`)
      .then(async res => {
        if (res.ok) {
          setStatus('success');
        } else {
          setMessage(await readApiError(res));
          setStatus('error');
        }
      })
      .catch(() => { setStatus('error'); setMessage('Something went wrong. Please try again.'); });
  }, [token]);

  return (
    <main className="auth-screen">
      <section className="auth-panel">
        <div className="auth-brand">
          <img src="/logo_only.svg" alt="Aroidex" className="auth-logo" />
          <div><h1>Aroidex</h1><p>Email verification</p></div>
        </div>
        {status === 'loading' && (
          <div className="auth-form">
            <p className="auth-reset-sent">Verifying your email…</p>
          </div>
        )}
        {status === 'success' && (
          <div className="auth-form">
            <p className="auth-reset-sent">Your email address has been verified.</p>
            <button type="button" className="btn btn-primary auth-submit" onClick={() => navigate('/', { replace: true })}>
              Continue to app
            </button>
          </div>
        )}
        {status === 'error' && (
          <div className="auth-form">
            <p className="auth-error">{message || 'Verification link is invalid or has expired.'}</p>
            <button type="button" className="btn btn-primary auth-submit" onClick={() => navigate('/', { replace: true })}>
              Go to app
            </button>
          </div>
        )}
      </section>
    </main>
  );
}

function AccessBanner({ accessNotice, onDismiss }) {
  if (!accessNotice) return null;
  return (
    <div className="access-banner" role="status">
      <span>{accessNotice.message}</span>
      <button type="button" onClick={onDismiss} aria-label="Dismiss" title="Dismiss"><Icon name="x" size={14} /></button>
    </div>
  );
}

// ─── App ───────────────────────────────────────────────────────────────────

function VerifyBanner({ session }) {
  const DISMISS_KEY = 'verifyBannerDismissed';
  const [dismissed, setDismissed] = useState(() => sessionStorage.getItem(DISMISS_KEY) === '1');
  const [resendStatus, setResendStatus] = useState('idle'); // 'idle' | 'sending' | 'sent'

  if (session?.user?.email_verified_at || dismissed) return null;

  async function resend() {
    setResendStatus('sending');
    try {
      await apiFetch('/auth/resend-verification', { method: 'POST' });
    } finally {
      setResendStatus('sent');
    }
  }

  function dismiss() {
    sessionStorage.setItem(DISMISS_KEY, '1');
    setDismissed(true);
  }

  return (
    <div className="verify-banner" role="status">
      <span>
        {resendStatus === 'sent'
          ? 'Verification email sent — check your inbox.'
          : <>Please verify your email address.{' '}
              <button
                type="button"
                className="verify-banner-resend"
                onClick={resend}
                disabled={resendStatus === 'sending'}
              >
                {resendStatus === 'sending' ? 'Sending…' : 'Resend email'}
              </button>
            </>
        }
      </span>
      <button type="button" className="verify-banner-dismiss" onClick={dismiss} aria-label="Dismiss" title="Dismiss"><Icon name="x" size={14} /></button>
    </div>
  );
}

function AppShell({ session, onSessionChange, onLogout }) {
  const [listResetKey, setListResetKey] = useState(0);

  const handleClearCollection = () => {
    sessionStorage.setItem('plantListState', JSON.stringify({
      search: '', sortField: 'genus', sortDir: 'asc', filtersOpen: false,
      statusFilter: [], methodFilter: [], substrateFilter: [],
      locationFilter: [], lightFilter: [], genusFilter: [],
    }));
    setListResetKey(k => k + 1);
  };

  return (
    <GuidanceProvider session={session} onSessionChange={onSessionChange}>
      <div className="app-shell">
        <VerifyBanner session={session} />
        <div className="app-scroll-area">
          <RouteTransition>
            <Routes>
              <Route path="/"              element={<PlantList key={listResetKey} />} />
              <Route path="/new"           element={<AddPlant />} />
              <Route path="/new/wishlist"  element={<PlantForm wishlistMode />} />
              <Route path="/new/manual"    element={<PlantForm />} />
              <Route path="/plants/:id"    element={<PlantDetail />} />
              <Route path="/plants/:id/edit" element={<PlantForm />} />
              <Route path="/stats"         element={<Stats />} />
              <Route path="/settings"      element={<Settings session={session} onSessionChange={onSessionChange} onLogout={onLogout} />} />
              <Route path="/settings/field-options" element={<FieldOptions />} />
              <Route path="/settings/household-admin" element={<HouseholdAdmin session={session} onSessionChange={onSessionChange} />} />
              <Route path="/settings/export" element={<ExportData session={session} />} />
              <Route path="/settings/notifications" element={<NotificationSettings session={session} />} />
              <Route path="/wishlist"      element={<Wishlist />} />
              <Route path="/archive"       element={<Archive />} />
              <Route path="/activity"      element={<Activity />} />
              <Route path="/health"        element={<DataHealth />} />
              <Route path="/rounds"        element={<Rounds />} />
              <Route path="/propagation"   element={<PropagationBay />} />
              <Route path="/plan"          element={<Plan />} />
              <Route path="/qa"                            element={<QAChecklist />} />
              <Route path="/admin"                         element={<OperatorConsole session={session} />} />
              <Route path="/admin/care-profiles"           element={<CareProfilesPage session={session} />} />
              <Route path="/admin/trait-review"           element={session?.user?.is_superadmin ? <TraitReview session={session} /> : <Navigate to="/" replace />} />
              <Route path="/explore"                       element={<Explore />} />
              <Route path="/explore/:genus/:species"       element={<ExploreDetail />} />
              <Route path="/compare"                       element={<Compare />} />
              <Route path="*"                              element={<Navigate to="/" replace />} />
            </Routes>
          </RouteTransition>
        </div>
        <BottomNav onClearCollection={handleClearCollection} />
      </div>
    </GuidanceProvider>
  );
}

function AuthGate() {
  const location = useLocation();
  const [session, setSession] = useState(null);
  const [checkingSession, setCheckingSession] = useState(true);
  const [accessNotice, setAccessNotice] = useState(null);
  const { setDateFmt } = useDateFormat();

  const setSessionAndFmt = (s) => {
    setSession(s);
    if (s?.user?.date_format) setDateFmt(s.user.date_format);
  };

  useEffect(() => {
    apiFetch('/auth/session')
      .then(async res => {
        if (res.ok) {
          setSessionAndFmt(await res.json());
        } else if (res.status !== 401) {
          setAccessNotice({ type: 'error', message: await readApiError(res) });
        }
      })
      .catch(() => {})
      .finally(() => setCheckingSession(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const originalFetch = window.fetch.bind(window);
    window.fetch = async (input, options) => {
      const response = await originalFetch(input, { credentials: 'same-origin', ...(options || {}) });
      const url = typeof input === 'string' ? input : input?.url || '';
      const isAuthRoute = url.startsWith('/auth/login') ||
        url.startsWith('/auth/signup') ||
        url.startsWith('/auth/logout') ||
        url.startsWith('/auth/session') ||
        url.startsWith('/auth/config') ||
        url.startsWith('/auth/password-reset/') ||
        url.startsWith('/auth/verify-email');
      if (!isAuthRoute && response.status === 401) {
        setSession(null);
        setAccessNotice({ type: 'auth', message: 'Your session has ended. Please log in again.' });
      } else if (!isAuthRoute && response.status === 403) {
        setAccessNotice({ type: 'forbidden', message: 'You do not have permission for that action.' });
      }
      return response;
    };
    return () => {
      window.fetch = originalFetch;
    };
  }, []);

  async function logout() {
    await apiFetch('/auth/logout', { method: 'POST' }).catch(() => {});
    sessionStorage.removeItem('aroidex_first_login');
    setSession(null);
  }

  // Public screens accessible without a session
  if (location.pathname === '/reset-password') {
    return <ResetPasswordScreen onAuthenticated={setSessionAndFmt} />;
  }
  if (location.pathname === '/verify-email') {
    return <VerifyEmailScreen />;
  }

  if (checkingSession) {
    return (
      <main className="auth-screen">
        <section className="auth-panel">
          <div className="auth-brand">
            <img src="/logo_only.svg" alt="Aroidex" className="auth-logo" />
            <div><h1>Aroidex</h1><p>Loading…</p></div>
          </div>
        </section>
      </main>
    );
  }

  return (
    <AuthContext.Provider value={{ session, setSession: setSessionAndFmt }}>
      <AccessBanner accessNotice={accessNotice} onDismiss={() => setAccessNotice(null)} />
      {session ? (
        <AppShell
          key={session.active_household?.id || 'household'}
          session={session}
          onSessionChange={setSessionAndFmt}
          onLogout={logout}
        />
      ) : (
        <AuthScreen onAuthenticated={setSessionAndFmt} />
      )}
    </AuthContext.Provider>
  );
}

// ─── Update Banner ─────────────────────────────────────────────────────────
// Detects when a new service worker is waiting and prompts the user to update.
// On confirm: sends SKIP_WAITING to the worker, then reloads to pick up changes.

function UpdateBanner() {
  const [waiting, setWaiting] = useState(null);

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;

    const checkForWaiting = (reg) => {
      if (reg.waiting) {
        setWaiting(reg.waiting);
      }
    };

    navigator.serviceWorker.getRegistration().then((reg) => {
      if (!reg) return;
      checkForWaiting(reg);

      // New worker found while page is open
      reg.addEventListener('updatefound', () => {
        const newWorker = reg.installing;
        if (!newWorker) return;
        newWorker.addEventListener('statechange', () => {
          if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
            setWaiting(newWorker);
          }
        });
      });
    });

    // Reload all tabs when the new worker takes control
    let reloading = false;
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (!reloading) { reloading = true; window.location.reload(); }
    });
  }, []);

  if (!waiting) return null;

  const handleUpdate = () => {
    waiting.postMessage({ type: 'SKIP_WAITING' });
  };

  return (
    <div className="update-banner">
      <span className="update-banner-text">A new version is available</span>
      <button className="update-banner-btn" onClick={handleUpdate}>
        Update now
      </button>
    </div>
  );
}

function App() {
  return (
    <ThemeProvider>
      <DateFormatProvider>
        <BrowserRouter>
          <UpdateBanner />
          <AuthGate />
        </BrowserRouter>
      </DateFormatProvider>
    </ThemeProvider>
  );
}

export default App;
